import { useLayoutEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ExplodingCoreScene } from '@/components/three/ExplodingCoreScene';
import { Counter } from '@/components/Counter';
import { STATS } from '@/data/content';
import { usePerfTier } from '@/lib/usePerfTier';

gsap.registerPlugin(ScrollTrigger);

const STRENGTH_POINTS = [
  'Strong presence across Hyderabad',
  'Experienced engineering team',
  'Robust legal verification process',
  'Transparent business practices',
];

/* Card animation variants */
const cardVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.92, rotateX: -12 },
  visible: (i) => ({
    opacity: 1, y: 0, scale: 1, rotateX: 0,
    transition: {
      duration: 0.9,
      delay: i * 0.15,
      ease: [0.16, 1, 0.3, 1],
    },
  }),
};

/* Bullet point variants */
const bulletVariants = {
  hidden: { opacity: 0, x: -16 },
  visible: (i) => ({
    opacity: 1, x: 0,
    transition: {
      duration: 0.6,
      delay: 0.5 + i * 0.08,
      ease: [0.16, 1, 0.3, 1],
    },
  }),
};

export const Strength = () => {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const violetRef = useRef(null);
  const cyanRef = useRef(null);
  const progressRef = useRef(0);
  const statsContainerRef = useRef(null);
  const { isMobile, reduced, dpr } = usePerfTier();

  // Cards become visible as soon as section enters viewport
  const statsInView = useInView(statsContainerRef, { once: true, margin: '-5%' });

  useLayoutEffect(() => {
    const isShort = window.innerHeight < 780;
    const pin = !isMobile && !reduced && !isShort;
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: pinRef.current,
        start: pin ? 'top top' : 'top 70%',
        end: pin ? '+=230%' : 'bottom top',
        pin,
        onUpdate: (self) => {
          const p = self.progress;
          progressRef.current = p;
          if (violetRef.current) violetRef.current.style.opacity = String(0.55 * (1 - p));
          if (cyanRef.current) cyanRef.current.style.opacity = String(0.5 * p);
        },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, [isMobile, reduced]);

  return (
    <section ref={sectionRef} id="strength" data-testid="strength-section" className="relative bg-void">
      {/* Inline styles for shimmer hover effect */}
      <style>{`
        .strength-card {
          position: relative;
          overflow: hidden;
        }
        .strength-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 60%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(201, 151, 27, 0.06),
            transparent
          );
          transition: none;
          pointer-events: none;
          z-index: 1;
        }
        .strength-card:hover::before {
          animation: shimmer 0.8s ease-out forwards;
        }
        @keyframes shimmer {
          to { left: 150%; }
        }
        .strength-card .gold-accent {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          width: 0;
          background: linear-gradient(90deg, #C9971B, #1E3A7B);
          border-radius: 0 0 1rem 1rem;
          transition: width 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .strength-card:hover .gold-accent {
          width: 100%;
        }
      `}</style>

      <div ref={pinRef} className="relative min-h-screen [@media(min-width:768px)_and_(min-height:780px)]:h-screen [@media(min-width:768px)_and_(min-height:780px)]:overflow-hidden">
        {/* animated gradient backdrop: violet → cyan */}
        <div
          ref={violetRef}
          className="absolute inset-0 pointer-events-none transition-opacity duration-200"
          style={{ opacity: 0.55, background: 'radial-gradient(ellipse 70% 55% at 25% 40%, rgba(30,58,123,0.10), transparent 70%)' }}
        />
        <div
          ref={cyanRef}
          className="absolute inset-0 pointer-events-none transition-opacity duration-200"
          style={{ opacity: 0, background: 'radial-gradient(ellipse 70% 55% at 75% 55%, rgba(201,151,27,0.12), transparent 70%)' }}
        />

        <div className="absolute inset-0">
          <ExplodingCoreScene progressRef={progressRef} dpr={dpr} />
        </div>

        <div className="relative z-10 min-h-screen [@media(min-width:768px)_and_(min-height:780px)]:h-full [@media(min-width:768px)_and_(min-height:780px)]:min-h-0 flex flex-col justify-between gap-12 [@media(min-width:768px)_and_(min-height:780px)]:gap-0 section-pad py-24 [@media(min-width:768px)_and_(min-height:780px)]:py-28 pointer-events-none">
          {/* Header with staggered entrance */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="text-[11px] uppercase tracking-[0.4em] text-brand-gold mb-5"
            >
              02 — Our Strength
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="font-clash font-semibold tracking-tight leading-[1.05] text-4xl md:text-5xl text-ink max-w-xl"
            >
              The scale we've built,<br />
              <span className="text-ink/70">measured in trust.</span>
            </motion.h2>
          </div>

          {/* Stats cards — animate in as soon as section enters viewport */}
          <div
            ref={statsContainerRef}
            className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl pointer-events-auto"
            data-testid="strength-stats"
            style={{ perspective: '900px' }}
          >
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                custom={i}
                initial="hidden"
                animate={statsInView ? 'visible' : 'hidden'}
                variants={cardVariants}
                whileHover={{ y: -6, scale: 1.03, transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] } }}
                className="strength-card glass rounded-2xl p-6 md:p-8 cursor-default"
              >
                <p className="relative z-10 font-clash font-semibold text-4xl md:text-6xl text-ink leading-none">
                  <Counter to={s.value} suffix={s.suffix} start={statsInView} className="" />
                </p>
                <p className="relative z-10 mt-3 text-[10px] md:text-xs uppercase tracking-[0.25em] text-brand-gold">{s.label}</p>
                {/* Animated gold accent line at bottom */}
                <div className="gold-accent" />
              </motion.div>
            ))}
          </div>

          {/* Strength bullet points with staggered entrance */}
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            {STRENGTH_POINTS.map((pt, i) => (
              <motion.span
                key={pt}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={bulletVariants}
                className="text-[11px] md:text-xs text-ink/55 flex items-center gap-2"
              >
                <motion.span
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.6 + i * 0.08, type: 'spring', stiffness: 300 }}
                  className="w-1.5 h-1.5 rounded-full bg-brand-gold inline-block"
                />
                {pt}
              </motion.span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
